// V3 Presence Console — public profile + cards + directory + reviews
// esclusivamente su /api/v2/presence-* (cap_presence). Stessa console
// per ogni preset: hair/beauty/gym/restaurant/portale.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });

export function V3PresencePage() {
  const [profile, setProfile] = useState<Row | null>(null);
  const [cards, setCards] = useState<Row[]>([]);
  const [categories, setCategories] = useState<Row[]>([]);
  const [locations, setLocations] = useState<Row[]>([]);
  const [reviews, setReviews] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ display_name: '', tagline: '', description: '', cover_url: '', is_published: false });
  const [locQuery, setLocQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, c, cat, r] = await Promise.all([
        apiFetch('/api/v2/presence-profile'), apiFetch('/api/v2/presence-cards'),
        apiFetch('/api/v2/presence-categories'), apiFetch('/api/v2/presence-reviews'),
      ]);
      const prof = await p.json();
      setProfile(prof);
      if (prof) setForm({
        display_name: prof.display_name || '', tagline: prof.tagline || '',
        description: prof.description || '', cover_url: prof.cover_url || '',
        is_published: !!prof.is_published,
      });
      setCards(await c.json());
      setCategories(await cat.json());
      setReviews(await r.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const searchLocations = useCallback(async (q: string) => {
    if (q.length < 2) { setLocations([]); return; }
    const res = await apiFetch(`/api/v2/presence-locations?type=comune&q=${encodeURIComponent(q)}`);
    setLocations(await res.json());
  }, []);

  const saveProfile = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/presence-profile', {
        method: 'PUT',
        body: JSON.stringify({
          display_name: form.display_name || null, tagline: form.tagline || null,
          description: form.description || null, cover_url: form.cover_url || null,
          is_published: form.is_published,
          status: form.is_published ? 'published' : 'draft',
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(320px, 420px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Profilo pubblico</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={input} placeholder="Nome pubblico" value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            <input style={input} placeholder="Tagline" value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
            <textarea style={{ ...input, minHeight: 70 }} placeholder="Descrizione" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input style={input} placeholder="Cover URL" value={form.cover_url}
              onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
            <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
              Pubblicato in directory
            </label>
            <button style={btn()} disabled={saving} onClick={() => void saveProfile()}>
              {saving ? 'Salvataggio…' : 'Salva profilo'}
            </button>
          </div>
          {profile && (
            <p className="sk-muted" style={{ fontSize: 12, marginTop: 10 }}>
              Stato: {profile.status} · id {profile.id}
              {profile.attrs?.public_phone ? ` · ${profile.attrs.public_phone}` : ''}
            </p>
          )}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Directory — cerca località</h4>
          <input style={input} placeholder="Cerca comune…" value={locQuery}
            onChange={(e) => { setLocQuery(e.target.value); void searchLocations(e.target.value); }} />
          {locations.slice(0, 8).map((l) => (
            <div key={l.id} style={{ padding: '6px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              {l.name} <span style={{ color: '#6b7280' }}>({l.attrs?.provincia || l.attrs?.sigla || l.type})</span>
            </div>
          ))}
          <p className="sk-muted" style={{ fontSize: 12, marginTop: 8 }}>
            {categories.length} categorie in directory
          </p>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Cards vetrina ({cards.length})</h4>
          {cards.length === 0 && <p className="sk-muted">Nessuna card.</p>}
          {cards.slice(0, 15).map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{c.title || '—'}</strong>{c.location_name ? ` · ${c.location_name}` : ''}</span>
              <span style={{ color: '#6b7280' }}>{c.category_name || ''} · {c.status}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Categorie ({categories.length})</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.map((c) => (
              <span key={c.id} style={{ padding: '4px 10px', borderRadius: 20, background: '#f3f4f6', fontSize: 12 }}>
                {c.icon ? `${c.icon} ` : ''}{c.name}
              </span>
            ))}
          </div>
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Reviews ({reviews.length})</h4>
          {reviews.length === 0 && <p className="sk-muted">Nessuna review.</p>}
          {reviews.slice(0, 15).map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>{'★'.repeat(Math.round(Number(r.rating) || 0))} {(r.body || '').slice(0, 60)}</span>
              <span style={{ color: '#6b7280' }}>{r.status} · {String(r.published_at).slice(0, 10)}</span>
            </div>
          ))}
        </div>

        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2/presence-* · capability cap_presence · profilo pubblico, cards, directory, reviews
        </p>
      </div>
    </div>
  );
}
